/**
 * Essential Baggage Components for Travel Demo
 * 
 * Components for the baggage handoff workflow including item cards,
 * custody chain visualization, and share functionality.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { 
  Package,
  Pill,
  Accessibility,
  Shield,
  Eye,
  EyeOff,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  ArrowRight,
  Receipt,
  Share2,
  Download,
  Link as LinkIcon,
  Copy,
  Loader2,
  Users,
  Building2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============== DATA BOUNDARY NORMALIZATION ==============
/**
 * Normalize baggage actor access to ensure can_see/cannot_see are always arrays.
 * Handles both old field names (details_visible/details_excluded) and new (can_see/cannot_see).
 */
const normalizeBaggageAccess = (access) => ({
  ...access,
  can_see: access.can_see ?? access.details_visible ?? [],
  cannot_see: access.cannot_see ?? access.details_excluded ?? [],
  actor_role: access.actor_role ?? 'unknown'
});

// ============== SHARED COMPONENTS ==============

// Criticality badge for baggage items
export const CriticalityBadge = ({ criticality }) => {
  const config = {
    high: 'bg-red-500 text-white',
    medium: 'bg-amber-500 text-white',
    low: 'bg-slate-500 text-white'
  };
  return (
    <Badge className={config[criticality] || config.low}>
      {criticality?.charAt(0).toUpperCase() + criticality?.slice(1)}
    </Badge>
  );
};

// Status badge style helper
const getBaggageStatusStyle = (status, routePath) => {
  const isDelivered = status === 'destination_ready' || status === 'reunited';
  const isDelayed = status === 'delayed' || routePath === 'rerouted';
  
  if (isDelivered) return 'bg-emerald-500/20 text-emerald-400';
  if (isDelayed) return 'bg-amber-500/20 text-amber-400';
  return 'bg-cyan-500/20 text-cyan-400';
};

// Status badge for baggage items
export const BaggageStatusBadge = ({ status, routePath }) => {
  const statusLabels = {
    in_passenger_custody: 'In Your Custody',
    origin_support_custody: 'With Origin Support',
    handler_custody: 'In Transit',
    destination_ready: 'Ready for Collection',
    reunited: 'Reunited',
    delayed: 'Delayed'
  };
  
  const isRerouted = routePath === 'rerouted';
  const label = statusLabels[status] || status;
  
  return (
    <div className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${getBaggageStatusStyle(status, routePath)}`}>
      <Clock className="w-3 h-3" />
      {label}
      {isRerouted && <span className="text-amber-300">(was rerouted)</span>}
    </div>
  );
};

// Baggage item card
export const BaggageItemCard = ({ item }) => {
  const getItemIcon = (type) => {
    if (type?.toLowerCase().includes('medical') || type?.toLowerCase().includes('medication')) {
      return <Pill className="w-4 h-4 text-rose-400" />;
    }
    if (type?.toLowerCase().includes('wheelchair') || type?.toLowerCase().includes('mobility')) {
      return <Accessibility className="w-4 h-4 text-cyan-400" />;
    }
    return <Package className="w-4 h-4 text-slate-400" />;
  };

  return (
    <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          {getItemIcon(item.item_type)}
          <span className="font-medium text-white">{item.title}</span>
        </div>
        <CriticalityBadge criticality={item.criticality} />
      </div>
      <div className="text-xs text-slate-500 mb-2">{item.item_type}</div>
      <BaggageStatusBadge status={item.status} routePath={item.route_path} />
      
      <div className="mt-3 space-y-1 text-xs">
        <div className="flex justify-between">
          <span className="text-slate-500">Current Custodian</span>
          <span className="text-white">{item.current_custodian}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-500">Location</span>
          <span className="text-slate-300">{item.current_location}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-500">Next</span>
          <span className="text-cyan-400">{item.next_handoff}</span>
        </div>
      </div>
      
      {item.handling_requirements && item.handling_requirements.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-700/50">
          <div className="text-xs text-slate-500 mb-2">Handling Requirements:</div>
          <div className="flex flex-wrap gap-1">
            {item.handling_requirements.map((req) => (
              <Badge key={`${item.item_id}-${req}`} variant="outline" className="text-xs border-slate-600 text-slate-400">
                {req}
              </Badge>
            ))}
          </div>
        </div>
      )}
      
      {item.notes && (
        <div className="mt-2 text-xs text-slate-400 italic">
          Note: {item.notes}
        </div>
      )}
    </div>
  );
};

// Custody chain visualization
export const CustodyChainView = ({ custodyChain }) => (
  <div className="space-y-3">
    {custodyChain?.map((record, i) => (
      <div key={record.record_id || i} className="flex gap-3">
        <div className="flex flex-col items-center">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
            record.is_delay_record 
              ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
              : 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
          }`}>
            {i + 1}
          </div>
          {i < custodyChain.length - 1 && (
            <div className="w-0.5 h-8 bg-slate-700 my-1" />
          )}
        </div>
        <div className="flex-1 pb-3">
          <div className="text-sm font-medium text-white">{record.custody_location}</div>
          <div className="text-xs text-cyan-400">{record.custodian}</div>
          <div className="text-xs text-slate-400 mt-1">{record.action}</div>
          {record.is_delay_record && (
            <Badge className="mt-1 bg-amber-500/20 text-amber-400 text-xs">
              Handling Delay
            </Badge>
          )}
        </div>
      </div>
    ))}
  </div>
);

// Baggage actor access card
export const BaggageActorCard = ({ access: rawAccess }) => {
  const access = normalizeBaggageAccess(rawAccess);
  return (
    <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${
            access.actor_role === 'Passenger' ? 'bg-cyan-400' : 'bg-emerald-400'
          }`} />
          <span className="font-medium text-white">{access.actor_name}</span>
        </div>
        <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
          {access.actor_role}
        </Badge>
      </div>
      <div className="text-xs text-slate-500 mb-3">{access.purpose}</div>
      <div className="space-y-2">
        <div>
          <div className="flex items-center gap-1 text-xs text-emerald-400 mb-1">
            <Eye className="w-3 h-3" />
            <span>May see:</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {access.can_see.map((item, i) => (
              <Badge key={`bag-see-${i}`} className="text-xs bg-emerald-500/10 text-emerald-400">
                {item}
              </Badge>
            ))}
          </div>
        </div>
        <div>
          <div className="flex items-center gap-1 text-xs text-rose-400 mb-1">
            <EyeOff className="w-3 h-3" />
            <span>Cannot see:</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {access.cannot_see.map((item, i) => (
              <Badge key={`bag-nosee-${i}`} className="text-xs bg-rose-500/10 text-rose-400">
                {item}
              </Badge>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-3 pt-2 border-t border-slate-700/50 text-xs text-slate-500">
        <Shield className="w-3 h-3 inline mr-1" />
        Authority: {access.authority_basis}
      </div>
    </div>
  );
};

// Baggage receipt entry
export const BaggageReceiptEntry = ({ entry, index }) => (
  <div className="p-3 bg-slate-800/30 rounded-lg border border-slate-700/50">
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2">
        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
          entry.is_delay_receipt 
            ? 'bg-amber-500/20 text-amber-400'
            : 'bg-cyan-500/20 text-cyan-400'
        }`}>
          {index + 1}
        </div>
        <span className="font-medium text-white text-sm">{entry.action}</span>
      </div>
      <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
        {entry.receipt_id}
      </Badge>
    </div>
    <div className="ml-8 space-y-1 text-xs">
      <div className="text-slate-400">
        <span className="text-slate-500">Custodian:</span> {entry.custodian}
      </div>
      {entry.items_involved && entry.items_involved.length > 0 && (
        <div className="flex items-start gap-1">
          <Package className="w-3 h-3 text-cyan-400 mt-0.5" />
          <span className="text-cyan-400">{entry.items_involved.join(', ')}</span>
        </div>
      )}
      {entry.data_withheld && entry.data_withheld.length > 0 && (
        <div className="flex items-start gap-1">
          <EyeOff className="w-3 h-3 text-rose-400 mt-0.5" />
          <span className="text-rose-400">Withheld: {entry.data_withheld.join(', ')}</span>
        </div>
      )}
      <div className="text-slate-500 font-mono text-xs truncate">
        Baggage ref: {entry.episode_ref}
      </div>
    </div>
  </div>
);

// Baggage delay banner
export const BaggageDelayBanner = ({ notice }) => {
  if (!notice) return null;
  
  return (
    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-medium text-amber-100">{notice.title}</div>
          <div className="text-sm text-amber-200/80 mt-1">{notice.message}</div>
          <div className="flex items-center gap-4 mt-2 text-xs">
            <span className="text-slate-400">
              Original ETA: <span className="text-slate-300">{notice.original_eta}</span>
            </span>
            <ArrowRight className="w-3 h-3 text-slate-500" />
            <span className="text-amber-400">
              Revised ETA: <span className="font-medium">{notice.revised_eta}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Baggage message bubble style helper
const getBaggageMessageStyle = (isUser, isAlert) => {
  if (isUser) return 'bg-cyan-600 text-white rounded-br-sm';
  if (isAlert) return 'bg-amber-500/20 text-amber-100 border border-amber-500/30 rounded-bl-sm';
  return 'bg-slate-800 text-white rounded-bl-sm';
};

// Baggage message bubble
export const BaggageMessageBubble = ({ message }) => {
  const isUser = message.is_user;
  
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] ${isUser ? 'order-2' : 'order-1'}`}>
        <div className={`rounded-2xl px-4 py-3 ${getBaggageMessageStyle(isUser, message.is_alert)}`}>
          {!isUser && (
            <div className={`text-xs mb-1 ${message.is_alert ? 'text-amber-400' : 'text-slate-400'}`}>
              {message.sender}
            </div>
          )}
          <div className="text-sm">{message.content}</div>
        </div>
        {message.governance_note && (
          <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
            <Shield className="w-3 h-3" />
            {message.governance_note}
          </div>
        )}
      </div>
    </div>
  );
};

// Baggage Receipt Pack Modal
export const BaggageReceiptModal = ({ isOpen, onClose, receipts }) => {
  if (!isOpen) return null;
  
  const handoffCount = receipts?.length || 0;
  const itemCount = new Set(receipts?.flatMap(r => r.items_involved || [])).size;
  const rerouteCount = receipts?.filter(r => r.is_delay_receipt).length || 0;
  
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-cyan-400" />
            <span className="font-semibold text-white">Baggage Evidence Pack</span>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-white">
            ✕
          </Button>
        </div>
        
        <div className="p-4 border-b border-slate-700 bg-slate-800/30">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-cyan-400">{handoffCount}</div>
              <div className="text-xs text-slate-500">Handoffs</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{itemCount}</div>
              <div className="text-xs text-slate-500">Items</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-amber-400">{rerouteCount}</div>
              <div className="text-xs text-slate-500">Rerouted</div>
            </div>
          </div>
        </div>
        
        <div className="p-4 overflow-y-auto max-h-[calc(80vh-200px)] space-y-3">
          {receipts?.map((entry, i) => (
            <BaggageReceiptEntry key={entry.receipt_id || i} entry={entry} index={i} />
          ))}
        </div>
        
        <div className="p-4 border-t border-slate-700 bg-slate-800/30">
          <div className="text-xs text-slate-500 text-center">
            All custody transfers recorded with baggage episode references.
          </div>
        </div>
      </div>
    </div>
  );
};

// Share Evidence Pack Modal
export const ShareEvidencePackModal = ({ isOpen, onClose }) => {
  const [selectedAudience, setSelectedAudience] = useState('passenger');
  const [shareLink, setShareLink] = useState(null);
  const [evidencePack, setEvidencePack] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('preview');

  const audiences = [
    { 
      id: 'passenger', 
      label: 'Passenger (Full View)', 
      icon: Users,
      description: 'Full visibility of your baggage, custody chain, and handling details'
    },
    { 
      id: 'insurer_support', 
      label: 'Insurer / Support', 
      icon: Building2,
      description: 'Claim-relevant info: status, delays, ETA changes. No handling specifics.'
    },
    { 
      id: 'operations', 
      label: 'Operations', 
      icon: Package,
      description: 'Handling requirements and logistics. No personal data.'
    }
  ];

  const [shareError, setShareError] = useState(null);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setShareError(null);
    try {
      const res = await fetch(`${API_URL}/api/governance/travel/baggage/evidence-pack?audience=${selectedAudience}`);
      if (res.ok) {
        const data = await res.json();
        setEvidencePack(data);
      } else {
        setShareError('Failed to load evidence pack');
      }
    } catch (err) {
      setShareError('Failed to load evidence pack');
    } finally {
      setLoading(false);
    }
  }, [selectedAudience]);

  useEffect(() => {
    if (isOpen) {
      loadPreview();
    }
  }, [isOpen, selectedAudience, loadPreview]);

  const createShareLink = async () => {
    setLoading(true);
    setShareError(null);
    try {
      const res = await fetch(`${API_URL}/api/governance/travel/baggage/share?audience=${selectedAudience}&expiry_hours=48`, {
        method: 'POST'
      });
      if (res.ok) {
        const data = await res.json();
        setShareLink(data);
      } else {
        setShareError('Failed to create share link');
      }
    } catch (err) {
      setShareError('Failed to create share link');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    if (shareLink) {
      navigator.clipboard.writeText(`${window.location.origin}${shareLink.share_url_hint}?audience=${selectedAudience}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const downloadPDF = async () => {
    try {
      const res = await fetch(`${API_URL}/api/governance/travel/baggage/evidence-pack/pdf-content?audience=${selectedAudience}`);
      if (res.ok) {
        const data = await res.json();
        const content = `
ESSENTIAL BAGGAGE EVIDENCE PACK
===============================
${data.subtitle}
Generated: ${new Date(data.generated).toLocaleString()}
Journey Reference: ${data.journey_ref}

SUMMARY
-------
Total Items: ${data.summary.total_items}
Delivered: ${data.summary.delivered}
Rerouted: ${data.summary.rerouted}
Custody Transfers: ${data.summary.custody_transfers}

ITEMS
-----
${data.items.map(item => `• ${item.title} (${item.type})
  Status: ${item.status}${item.rerouted ? ' [REROUTED]' : ''}
  ${item.criticality ? `Criticality: ${item.criticality}` : ''}
  ${item.handling ? `Handling: ${item.handling.join(', ')}` : ''}`).join('\n\n')}

CUSTODY CHAIN
-------------
${data.custody_chain.map(c => `${c.step}. ${c.location} - ${c.custodian}
   ${c.action}`).join('\n\n')}

DISCLOSURE NOTICE
-----------------
${data.disclosure_notice}

${data.visibility_note}

---
${data.footer}
        `;
        
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `baggage-evidence-${selectedAudience}-${Date.now()}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      setShareError('Failed to download evidence pack');
    }
  };

  if (!isOpen) return null;

  const selectedAudienceInfo = audiences.find(a => a.id === selectedAudience);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-3xl w-full max-h-[85vh] overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-cyan-400" />
            <span className="font-semibold text-white">Share Evidence Pack</span>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-white">
            ✕
          </Button>
        </div>

        {/* Error display */}
        {shareError && (
          <div className="p-3 bg-red-500/10 border-b border-red-500/30 text-red-400 text-sm">
            {shareError}
          </div>
        )}

        {/* Audience Selection */}
        <div className="p-4 border-b border-slate-700 bg-slate-800/30">
          <div className="text-sm text-slate-400 mb-3">Select audience (bounded disclosure applies):</div>
          <div className="grid grid-cols-3 gap-2">
            {audiences.map(aud => (
              <button
                key={aud.id}
                onClick={() => { setSelectedAudience(aud.id); setShareLink(null); }}
                className={`p-3 rounded-lg border text-left transition-colors ${
                  selectedAudience === aud.id
                    ? 'bg-cyan-500/20 border-cyan-500/50 text-white'
                    : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:border-slate-600'
                }`}
                data-testid={`audience-${aud.id}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <aud.icon className="w-4 h-4" />
                  <span className="font-medium text-sm">{aud.label}</span>
                </div>
                <div className="text-xs text-slate-500">{aud.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-700">
          <button
            onClick={() => setActiveTab('preview')}
            className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
              activeTab === 'preview' ? 'text-cyan-400 border-b-2 border-cyan-400' : 'text-slate-400 hover:text-white'
            }`}
          >
            Preview
          </button>
          <button
            onClick={() => setActiveTab('share')}
            className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
              activeTab === 'share' ? 'text-cyan-400 border-b-2 border-cyan-400' : 'text-slate-400 hover:text-white'
            }`}
          >
            Share Link
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(85vh-280px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
            </div>
          ) : activeTab === 'preview' && evidencePack ? (
            <div className="space-y-4">
              {/* Visibility Notice */}
              <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                <div className="flex items-start gap-2">
                  <Eye className="w-4 h-4 text-cyan-400 mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-white mb-1">{evidencePack.audience_label}</div>
                    <div className="text-xs text-slate-400">{evidencePack.visibility_note}</div>
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="grid grid-cols-4 gap-2">
                <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                  <div className="text-lg font-bold text-white">{evidencePack.summary.total_items}</div>
                  <div className="text-xs text-slate-500">Items</div>
                </div>
                <div className="text-center p-3 bg-emerald-500/10 rounded-lg">
                  <div className="text-lg font-bold text-emerald-400">{evidencePack.summary.items_delivered}</div>
                  <div className="text-xs text-slate-500">Delivered</div>
                </div>
                <div className="text-center p-3 bg-amber-500/10 rounded-lg">
                  <div className="text-lg font-bold text-amber-400">{evidencePack.summary.items_rerouted}</div>
                  <div className="text-xs text-slate-500">Rerouted</div>
                </div>
                <div className="text-center p-3 bg-cyan-500/10 rounded-lg">
                  <div className="text-lg font-bold text-cyan-400">{evidencePack.summary.custody_transfers}</div>
                  <div className="text-xs text-slate-500">Transfers</div>
                </div>
              </div>

              {/* Items */}
              <div>
                <div className="text-sm font-medium text-white mb-2">Items</div>
                <div className="space-y-2">
                  {evidencePack.items.map(item => (
                    <div key={item.item_id} className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-white">{item.title}</span>
                        <Badge className={item.was_rerouted ? 'bg-amber-500' : 'bg-emerald-500'}>
                          {item.status_label}
                        </Badge>
                      </div>
                      <div className="text-xs text-slate-500">{item.item_type}</div>
                      {item.criticality && (
                        <div className="text-xs text-slate-400 mt-1">Criticality: {item.criticality}</div>
                      )}
                      {item.handling_requirements && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {item.handling_requirements.map((req) => (
                            <Badge key={`${item.item_id}-${req}`} variant="outline" className="text-xs border-slate-600 text-slate-400">
                              {req}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Custody Chain */}
              <div>
                <div className="text-sm font-medium text-white mb-2">Custody Chain</div>
                <div className="space-y-2">
                  {evidencePack.custody_chain.map(record => (
                    <div key={record.step} className="flex gap-3 items-start">
                      <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400 text-xs font-medium flex-shrink-0">
                        {record.step}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm text-white">{record.location}</div>
                        <div className="text-xs text-slate-500">{record.custodian} — {record.action}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Disclosure Notice */}
              <div className="p-3 bg-slate-800/30 rounded-lg border border-slate-700/50">
                <div className="text-xs text-slate-400">{evidencePack.disclosure_notice}</div>
              </div>
            </div>
          ) : activeTab === 'share' ? (
            <div className="space-y-4">
              {/* Share Link Section */}
              <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                <div className="text-sm font-medium text-white mb-2">Create Expiring Share Link</div>
                <div className="text-xs text-slate-400 mb-4">
                  Generate a time-limited link for {selectedAudienceInfo?.label}. Link expires in 48 hours and allows up to 10 accesses.
                </div>
                
                {shareLink ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 p-2 bg-slate-900 rounded border border-slate-700">
                      <LinkIcon className="w-4 h-4 text-cyan-400" />
                      <code className="flex-1 text-xs text-cyan-400 truncate">
                        {window.location.origin}{shareLink.share_url_hint}
                      </code>
                      <Button size="sm" variant="ghost" onClick={copyLink} className="text-slate-400 hover:text-white">
                        {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="text-slate-500">Share ID: <span className="text-slate-300">{shareLink.share_id}</span></div>
                      <div className="text-slate-500">Expires: <span className="text-amber-400">{new Date(shareLink.expires_at).toLocaleString()}</span></div>
                    </div>
                  </div>
                ) : (
                  <Button onClick={createShareLink} className="bg-cyan-600 hover:bg-cyan-700" data-testid="create-share-link-btn">
                    <LinkIcon className="w-4 h-4 mr-2" />
                    Create Share Link
                  </Button>
                )}
              </div>

              {/* Download Section */}
              <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                <div className="text-sm font-medium text-white mb-2">Download Evidence Pack</div>
                <div className="text-xs text-slate-400 mb-4">
                  Download a copy of the evidence pack for {selectedAudienceInfo?.label}. Bounded disclosure is preserved.
                </div>
                <Button onClick={downloadPDF} variant="outline" className="border-slate-600 text-slate-300 hover:text-white" data-testid="download-evidence-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Download Evidence Pack
                </Button>
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-700 bg-slate-800/30">
          <div className="text-xs text-slate-500 text-center">
            Shared under KATLAS governance with bounded disclosure. Each audience sees only what you authorised.
          </div>
        </div>
      </div>
    </div>
  );
};
